/**
 * Pipelines · Paneles / Dashboards (Fase 4).
 *
 * Permite armar paneles con widgets gráficos sobre los pipelines:
 *  - KPI: número grande (total/suma/promedio/ganados)
 *  - Bar by stage: barras por etapa
 *  - Bar by owner: barras por propietario
 *  - Line by month: tendencia por mes (creación)
 *  - Funnel: embudo (etapas en orden)
 *  - Pie by field: torta por campo personalizado
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { BarChart3, BarChart2, Hash, PieChart as PieIcon, Activity, TrendingUp, Filter, Table as TableIcon, Plus, Trash2, Edit3, Loader2, Save, X, LayoutDashboard, RefreshCw, Database, Download, Search as SearchIcon, MoreVertical, Copy, Type as TypeIcon, Settings, Move, Target, Sparkles, Tag as TagIcon, ArrowRightLeft, Image as ImageIcon, FileText, ListChecks } from "lucide-react";
import PanelTemplatesDialog from "./PanelTemplatesDialog";
import { VISIBILITY_OPTIONS, RELATIVE_RANGES, visibilityLabel } from "./panelTemplates";
import { toast } from "sonner";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, FunnelChart, Funnel, LabelList,
  AreaChart, Area, Legend,
} from "recharts";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WIDGET_TYPES = [
  { value: "kpi", label: "Número grande", icon: Hash },
  { value: "bar_by_stage", label: "Barras horizontales", icon: BarChart3 },
  { value: "bar_vertical", label: "Barras verticales", icon: BarChart2 },
  { value: "bar_by_owner", label: "Barras por propietario", icon: BarChart2 },
  { value: "bar_by_field", label: "Barras por campo", icon: BarChart2 },
  { value: "line_by_month", label: "Línea (tendencia)", icon: TrendingUp },
  { value: "area_by_month", label: "Área", icon: Activity },
  { value: "funnel", label: "Embudo", icon: Filter },
  { value: "pie_by_field", label: "Torta", icon: PieIcon },
  { value: "donut", label: "Anillo (donut)", icon: PieIcon },
  { value: "goal_vs_actual", label: "Meta vs Logrado", icon: Target },
  { value: "table", label: "Tabla", icon: TableIcon },
  { value: "tasks_summary", label: "Resumen de tareas", icon: ListChecks },
];
const GROUPABLE_TYPES = ["pie_by_field", "donut", "bar_by_field", "bar_by_stage", "bar_vertical", "bar_by_owner", "funnel", "table"];
const TABLE_COLUMNS = [
  { key: "count", label: "Cantidad" },
  { key: "sum_value", label: "Valor total" },
  { key: "avg_value", label: "Prima promedio" },
  { key: "won_count", label: "Ganados" },
  { key: "won_value", label: "Valor ganado" },
  { key: "pct", label: "%" },
];
const DEFAULT_TABLE_COLS = ["count", "sum_value", "pct"];
const METRICS = [
  { value: "count", label: "Cantidad de tarjetas" },
  { value: "sum_value", label: "Suma de valor" },
  { value: "avg_value", label: "Promedio de valor" },
  { value: "won_count", label: "Cantidad ganadas" },
  { value: "won_value", label: "Valor ganado" },
  { value: "weighted_value", label: "Valor ponderado (prob.)" },
  { value: "won_rate", label: "Tasa de conversión %" },
];
// Agrupaciones estándar (no campos personalizados) para torta/donut
const STD_GROUP_OPTIONS = [
  { value: "lost_reason", label: "Motivo de pérdida" },
  { value: "status", label: "Status (ganada/perdida/abierta)" },
  { value: "owner_name", label: "Propietario / vendedor" },
];
const PIE_COLORS = ["#193966", "#80BC00", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#10B981", "#EC4899", "#6366F1", "#14B8A6"];
const fmt = (n) => Number(n || 0).toLocaleString("es-DO", { maximumFractionDigits: 0 });

export default function PanelesPage() {
  const { getAuthHeaders, user } = useAuth();
  const [paneles, setPaneles] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [showMetas, setShowMetas] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  // Tab activa: pipeline_id ó "__none__" para paneles sin pipeline.
  const [activePipelineTab, setActivePipelineTab] = useState(null);
  const [selectedPanelId, setSelectedPanelId] = useState("all");
  useEffect(() => { setSelectedPanelId("all"); }, [activePipelineTab]);
  // Data computada por panel_id => widgets_data
  const [dataByPanel, setDataByPanel] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [users, setUsers] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/users`, getAuthHeaders());
      const d = await r.json();
      const us = Array.isArray(d) ? d : (d.users || []);
      setUsers(us.filter((u) => u.is_active !== false));
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchPipelines = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines`, getAuthHeaders());
    const d = await r.json();
    setPipelines(d.items || []);
  }, [getAuthHeaders]);

  const fetchPaneles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles`, getAuthHeaders());
      const d = await r.json();
      setPaneles(d.items || []);
    } catch {
      toast.error("Error al cargar paneles");
    }
    setLoading(false);
  }, [getAuthHeaders]);

  // Carga la data (widgets_data) de UN panel — usado al expandir o refrescar.
  // qs opcional: filtros rápidos a nivel panel (owner_id / created_range).
  const fetchPanelData = useCallback(async (panelId, qs = "") => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panelId}/data${qs ? `?${qs}` : ""}`, getAuthHeaders());
      const d = await r.json();
      setDataByPanel((prev) => ({ ...prev, [panelId]: d.widgets_data || {} }));
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => { fetchPipelines(); fetchPaneles(); fetchUsers(); }, [fetchPipelines, fetchPaneles, fetchUsers]);

  // Listen for inline action events (clone/rename) that need to refresh the list
  useEffect(() => {
    const handler = () => fetchPaneles();
    window.addEventListener("paneles:reload", handler);
    return () => window.removeEventListener("paneles:reload", handler);
  }, [fetchPaneles]);

  // Agrupar paneles por pipeline_id
  const panelesByPipeline = useMemo(() => {
    const map = {};
    for (const p of paneles) {
      const key = p.pipeline_id || "__none__";
      (map[key] = map[key] || []).push(p);
    }
    return map;
  }, [paneles]);

  // Tabs: solo pipelines que tienen paneles + "Sin pipeline" si aplica
  const visibleTabs = useMemo(() => {
    const tabs = [];
    for (const pl of pipelines) {
      if (panelesByPipeline[pl.id]?.length) {
        tabs.push({ id: pl.id, name: pl.name, count: panelesByPipeline[pl.id].length });
      }
    }
    if (panelesByPipeline["__none__"]?.length) {
      tabs.push({ id: "__none__", name: "Sin pipeline", count: panelesByPipeline["__none__"].length });
    }
    return tabs;
  }, [pipelines, panelesByPipeline]);

  // Auto-seleccionar primer tab disponible
  useEffect(() => {
    if (!activePipelineTab && visibleTabs.length) {
      setActivePipelineTab(visibleTabs[0].id);
    } else if (activePipelineTab && !visibleTabs.find(t => t.id === activePipelineTab)) {
      setActivePipelineTab(visibleTabs[0]?.id || null);
    }
  }, [visibleTabs, activePipelineTab]);

  // Cargar data de TODOS los paneles del tab activo automáticamente
  useEffect(() => {
    if (!activePipelineTab) return;
    const list = panelesByPipeline[activePipelineTab] || [];
    for (const p of list) {
      if (!dataByPanel[p.id]) fetchPanelData(p.id);
    }
  }, [activePipelineTab, panelesByPipeline, fetchPanelData, dataByPanel]);

  const handleSave = async (panel) => {
    const isNew = !panel.id;
    const url = isNew ? `${API_URL}/api/pipelines/paneles` : `${API_URL}/api/pipelines/paneles/${panel.id}`;
    const method = isNew ? "POST" : "PUT";
    try {
      const r = await fetch(url, {
        method,
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: panel.name,
          description: panel.description || "",
          pipeline_id: panel.pipeline_id || null,
          widgets: panel.widgets || [],
          is_shared: !!panel.is_shared,
          visibility: panel.visibility || (panel.is_shared ? "shared_view" : "private"),
          allowed_user_ids: panel.allowed_user_ids || [],
          tags: panel.tags || [],
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      const result = await r.json();
      toast.success(isNew ? "Panel creado" : "Panel actualizado");
      await fetchPaneles();
      // Refrescar la data del panel guardado
      const panelId = isNew ? result.id : panel.id;
      if (panelId) fetchPanelData(panelId);
      // Saltar al tab del pipeline del panel guardado para que el user lo vea de inmediato
      const newPipelineId = result.pipeline_id || panel.pipeline_id || (panel.widgets?.[0]?.pipeline_id);
      if (newPipelineId) setActivePipelineTab(newPipelineId);
      setShowEditor(false);
      setEditing(null);
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const handleDelete = async (panel) => {
    if (!window.confirm(`¿Eliminar el panel "${panel.name}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      if (!r.ok) throw new Error();
      toast.success("Panel eliminado");
      fetchPaneles();
    } catch { toast.error("Error al eliminar"); }
  };

  const panelsToShow = activePipelineTab ? (panelesByPipeline[activePipelineTab] || []) : [];

  const currentPipelineId = (activePipelineTab && activePipelineTab !== "__none__")
    ? activePipelineTab
    : (pipelines[0]?.id || null);

  const handleSeedCrm = async () => {
    if (!currentPipelineId) { toast.error("Seleccioná un pipeline primero"); return; }
    setSeeding(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${currentPipelineId}/seed-crm-comercial`, { method: "POST", headers: getAuthHeaders().headers });
      if (!r.ok) throw new Error();
      const d = await r.json();
      const parts = [];
      if (d.fields_created?.length) parts.push(`${d.fields_created.length} campo(s) comercial(es)`);
      if (d.panel_created) parts.push("tablero CRM creado");
      toast.success(parts.length ? `CRM Comercial configurado: ${parts.join(" · ")}` : "El CRM Comercial ya estaba configurado");
      fetchPaneles();
    } catch { toast.error("Error al configurar CRM Comercial"); }
    finally { setSeeding(false); }
  };


  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 overflow-x-hidden">
        <div className="p-4 pt-14 md:pt-4 max-w-[1800px] mx-auto" data-testid="paneles-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#193966]">Paneles</h1>
            <p className="text-xs text-slate-500">Constructor de gráficos sobre tus pipelines</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={handleSeedCrm} disabled={seeding} className="h-9" data-testid="seed-crm-btn">
                {seeding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5 text-[#80BC00]" />} Configurar CRM Comercial
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowMetas(true)} className="h-9" data-testid="metas-btn">
                <Target className="w-4 h-4 mr-1.5 text-[#193966]" /> Metas de venta
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => setShowTemplates(true)} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="paneles-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Crear panel
          </Button>
        </div>
      </div>

      {/* Tabs por pipeline */}
      {visibleTabs.length > 0 && (
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200 flex-wrap">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActivePipelineTab(t.id)}
              className={`h-9 px-4 text-sm font-semibold border-b-2 transition-colors -mb-px ${activePipelineTab === t.id ? "border-[#193966] text-[#193966]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`paneles-tab-${t.id}`}
            >
              {t.name}
              <span className="ml-1.5 text-[10px] bg-slate-200 text-slate-700 rounded-full px-1.5 py-0.5">{t.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Selector de panel: ver uno a ancho completo o todos en cuadrícula */}
      {!loading && panelsToShow.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="panel-selector-row">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Ver panel:</span>
          <select
            value={selectedPanelId}
            onChange={(e) => setSelectedPanelId(e.target.value)}
            className="h-9 border border-slate-300 rounded px-2 text-sm bg-white min-w-[260px] font-medium text-[#193966]"
            data-testid="panel-selector"
          >
            <option value="all">Todos los paneles (cuadrícula)</option>
            {panelsToShow.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {selectedPanelId !== "all" && (
            <span className="text-[11px] text-slate-400">Vista a ancho completo · los gráficos se ven grandes</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center p-12"><Loader2 className="w-7 h-7 mx-auto animate-spin text-slate-400" /></div>
      ) : paneles.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center bg-slate-50">
          <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-700 mb-2">Empezá creando tu primer panel</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">
            Combiná KPIs, barras, embudos y tortas a partir de tus pipelines.
          </p>
          <Button onClick={() => setShowTemplates(true)} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="paneles-empty-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Crear panel
          </Button>
        </div>
      ) : selectedPanelId !== "all" && panelsToShow.some((p) => p.id === selectedPanelId) ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="panel-fullwidth">
          <PanelCard
            panel={panelsToShow.find((p) => p.id === selectedPanelId)}
            widgetsData={dataByPanel[selectedPanelId] || {}}
            pipelines={pipelines}
            users={users}
            onRefresh={(qs) => fetchPanelData(selectedPanelId, qs)}
            onWidgetChange={() => { fetchPaneles(); fetchPanelData(selectedPanelId); }}
            onEdit={() => { setEditing(panelsToShow.find((p) => p.id === selectedPanelId)); setShowEditor(true); }}
            onDelete={() => handleDelete(panelsToShow.find((p) => p.id === selectedPanelId))}
            fullWidth
          />
        </div>
      ) : (
        <PanelsGridLayout
          panels={panelsToShow}
          dataByPanel={dataByPanel}
          pipelines={pipelines}
          users={users}
          onRefresh={(pid, qs) => fetchPanelData(pid, qs)}
          onWidgetChange={(pid) => { fetchPaneles(); fetchPanelData(pid); }}
          onEdit={(p) => { setEditing(p); setShowEditor(true); }}
          onDelete={(p) => handleDelete(p)}
          onLayoutSaved={fetchPaneles}
        />
      )}

      {showTemplates && (
        <PanelTemplatesDialog
          pipelines={pipelines}
          users={users}
          defaultPipelineId={activePipelineTab !== "__none__" ? activePipelineTab : pipelines[0]?.id}
          onClose={() => setShowTemplates(false)}
          onFromScratch={() => {
            setShowTemplates(false);
            setEditing({ name: "", description: "", widgets: [], is_shared: false, visibility: "private", allowed_user_ids: [], tags: [], pipeline_id: activePipelineTab !== "__none__" ? activePipelineTab : null });
            setShowEditor(true);
          }}
          onCreate={(panelObj) => { setShowTemplates(false); handleSave(panelObj); }}
        />
      )}

      {showEditor && (
        <PanelEditorDialog
          panel={editing}
          pipelines={pipelines}
          users={users}
          onClose={() => { setShowEditor(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      {showMetas && (
        <MetasDialog
          pipelineId={currentPipelineId}
          pipelineName={pipelines.find((p) => p.id === currentPipelineId)?.name}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setShowMetas(false)}
        />
      )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// WIDGET RENDERER
// ============================================================
function WidgetRenderer({ widget, result, onSegmentClick }) {
  const handleClick = (data) => {
    if (!onSegmentClick) return;
    // Recharts pasa el data object con label/name del segmento clickeado
    const label = data?.label ?? data?.name ?? data?.payload?.label ?? data?.payload?.name;
    if (label) onSegmentClick(String(label));
  };
  if (!result) {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      </div>
    );
  }
  if (result.error) {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-rose-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <p className="text-[11px] text-rose-500">{result.error}</p>
      </div>
    );
  }

  if (widget.type === "kpi") {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 flex flex-col justify-center">
          <p className="text-4xl font-bold" style={{ color: widget.color || "#193966" }}>{fmt(result.value)}</p>
          <p className="text-[10px] text-slate-400 mt-1">{METRICS.find(m => m.value === widget.metric)?.label || widget.metric}</p>
        </div>
      </div>
    );
  }

  const dataArr = result.data || [];

  if (widget.type === "goal_vs_actual") {
    const gData = result.data || [];
    if (!gData.length) {
      return (
        <div className="h-full flex flex-col">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <Target className="w-8 h-8 text-slate-300 mb-2" />
            <p className="text-sm font-semibold text-slate-700">Sin metas configuradas</p>
            <p className="text-[11px] text-slate-500 mt-1">Definí las metas mensuales por vendedor para ver el avance.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
          {widget.title}{result.period && <span className="ml-1 text-slate-400 font-normal">· {result.period}</span>}
        </p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={gData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="meta" name="Meta" fill="#94A3B8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual" name="Logrado" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "tasks_summary") {
    const counts = result.counts || {};
    const tList = result.tasks || [];
    const SLA_CHIP = {
      vencida: ["Vencida", "bg-red-100 text-red-700 border-red-300"],
      por_vencer: ["Por vencer", "bg-amber-100 text-amber-700 border-amber-300"],
    };
    return (
      <div className="h-full flex flex-col" data-testid={`tasks-summary-widget-${widget.id}`}>
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">{widget.title}</p>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid={`tasks-summary-vencidas-${widget.id}`}>
            Vencidas <span className="font-bold">{counts.vencida || 0}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`tasks-summary-porvencer-${widget.id}`}>
            Por vencer <span className="font-bold">{counts.por_vencer || 0}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            En tiempo <span className="font-bold">{counts.en_tiempo || 0}</span>
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1">
          {tList.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <ListChecks className="w-7 h-7 text-emerald-300 mb-1.5" />
              <p className="text-xs text-slate-500">Sin tareas vencidas ni por vencer en este pipeline.</p>
            </div>
          ) : tList.map((t) => {
            const [lbl, cls] = SLA_CHIP[t.sla_status] || ["", ""];
            return (
              <div key={t.id} className="flex items-center gap-2 border border-slate-100 rounded px-2 py-1 bg-slate-50/60" data-testid={`tasks-summary-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-slate-700 truncate">{t.title}</p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {t.card_title || "—"}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ""}{t.due_date ? ` · vence ${t.due_date}` : ""}
                  </p>
                </div>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0 ${cls}`}>{lbl}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Estado vacío amigable: data ausente o todos los valores en 0.
  const isEmptyData = (() => {
    if (widget.type === "table") return !((result.rows && result.rows.length) || (result.group_rows && result.group_rows.length));
    if (!Array.isArray(dataArr) || dataArr.length === 0) return true;
    return dataArr.every((d) => !d.value);
  })();

  if (isEmptyData) {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-2">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-2">
            <BarChart3 className="w-6 h-6 text-slate-400" />
          </div>
          <p className="text-sm font-semibold text-slate-700">Aún no hay datos</p>
          <p className="text-[11px] text-slate-500 mt-1 max-w-[260px]">
            Creá tu primera tarjeta en el pipeline para que aparezca en este gráfico.
          </p>
          <button
            type="button"
            onClick={() => window.location.assign("/pipelines")}
            className="mt-2 text-[11px] text-[#193966] hover:text-[#80BC00] font-semibold underline underline-offset-2"
            data-testid={`empty-cta-${widget.id}`}
          >
            Ir a Negocios →
          </button>
        </div>
      </div>
    );
  }

  if (widget.type === "bar_by_stage" || widget.type === "bar_by_owner" || widget.type === "bar_by_field") {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataArr} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} allowDecimals={false} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="value" fill={widget.color || "#193966"} onClick={handleClick} style={{ cursor: onSegmentClick ? "pointer" : "default" }}>
                {dataArr.map((d, i) => <Cell key={i} fill={d.color || PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "bar_vertical") {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataArr} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={100} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="value" onClick={handleClick} style={{ cursor: onSegmentClick ? "pointer" : "default" }}>
                {dataArr.map((d, i) => <Cell key={i} fill={d.color || PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "line_by_month") {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dataArr} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="value" stroke={widget.color || "#193966"} strokeWidth={2} dot={{ r: 3, onClick: handleClick, style: { cursor: onSegmentClick ? "pointer" : "default" } }} activeDot={{ r: 5, onClick: handleClick, style: { cursor: onSegmentClick ? "pointer" : "default" } }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "area_by_month") {
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dataArr} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
              <defs>
                <linearGradient id={`area-grad-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={widget.color || "#80BC00"} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={widget.color || "#80BC00"} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="value" stroke={widget.color || "#80BC00"} strokeWidth={2} fill={`url(#area-grad-${widget.id})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "funnel") {
    const funnelData = dataArr.map((d, i) => ({ name: d.label, value: d.value, fill: d.color || PIE_COLORS[i % PIE_COLORS.length] }));
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <FunnelChart>
              <Tooltip formatter={(v) => fmt(v)} />
              <Funnel dataKey="value" data={funnelData} isAnimationActive onClick={handleClick} style={{ cursor: onSegmentClick ? "pointer" : "default" }}>
                <LabelList position="right" fill="#475569" stroke="none" dataKey="name" fontSize={11} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "pie_by_field" || widget.type === "donut") {
    const innerR = widget.type === "donut" ? "55%" : 0;
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">{widget.title}</p>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 10 }} formatter={(value, entry) => `${value}: ${fmt(entry?.payload?.value)}`} />
              <Pie data={dataArr} dataKey="value" nameKey="label" innerRadius={innerR} outerRadius="70%" labelLine={false} onClick={handleClick} style={{ cursor: onSegmentClick ? "pointer" : "default" }}>
                {dataArr.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (widget.type === "table") {
    const grows = result.group_rows;
    if (Array.isArray(grows)) {
      const colKeys = (widget.table_columns && widget.table_columns.length ? widget.table_columns : DEFAULT_TABLE_COLS);
      const cols = TABLE_COLUMNS.filter((c) => colKeys.includes(c.key));
      const totals = {
        count: grows.reduce((a, r) => a + (r.count || 0), 0),
        sum_value: grows.reduce((a, r) => a + (r.sum_value || 0), 0),
        won_count: grows.reduce((a, r) => a + (r.won_count || 0), 0),
        won_value: grows.reduce((a, r) => a + (r.won_value || 0), 0),
      };
      totals.avg_value = totals.count ? totals.sum_value / totals.count : 0;
      totals.pct = "100%";
      const cellVal = (r, k) => (k === "pct" ? `${r.pct}%` : (k === "count" || k === "won_count") ? (r[k] || 0) : fmt(r[k] || 0));
      const totVal = (k) => (k === "pct" ? "100%" : (k === "count" || k === "won_count") ? totals[k] : fmt(totals[k] || 0));
      return (
        <div className="h-full flex flex-col">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
            {widget.title}
            {typeof result.total === "number" && <span className="ml-1 text-slate-400 font-normal">· {result.total} negocios</span>}
          </p>
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-[11px]" data-testid={`table-grouped-${widget.id}`}>
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 font-semibold">Grupo</th>
                  {cols.map((c) => <th key={c.key} className="text-right px-2 py-1 font-semibold">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {grows.length === 0 ? (
                  <tr><td colSpan={cols.length + 1} className="text-center text-slate-400 italic py-3">Sin datos</td></tr>
                ) : grows.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => onSegmentClick && onSegmentClick(r.label)}>
                    <td className="px-2 py-1 truncate max-w-[200px] font-medium text-slate-800">{r.label}</td>
                    {cols.map((c) => <td key={c.key} className="px-2 py-1 text-right tabular-nums">{cellVal(r, c.key)}</td>)}
                  </tr>
                ))}
              </tbody>
              {grows.length > 0 && (
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr>
                    <td className="px-2 py-1">Total</td>
                    {cols.map((c) => <td key={c.key} className="px-2 py-1 text-right tabular-nums">{totVal(c.key)}</td>)}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      );
    }
    const rows = result.rows || [];
    return (
      <div className="h-full flex flex-col">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
          {widget.title}
          {typeof result.total === "number" && <span className="ml-1 text-slate-400 font-normal">· {result.total} tarjetas</span>}
        </p>
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 border-b sticky top-0">
              <tr>
                <th className="text-left px-2 py-1 font-semibold">Negocio</th>
                <th className="text-left px-2 py-1 font-semibold">Etapa</th>
                <th className="text-left px-2 py-1 font-semibold">Propietario</th>
                <th className="text-right px-2 py-1 font-semibold">Valor</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-slate-400 italic py-3">Sin tarjetas</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => onSegmentClick && onSegmentClick(r.stage_name)}>
                  <td className="px-2 py-1 truncate max-w-[180px]">{r.title || "—"}</td>
                  <td className="px-2 py-1 text-slate-600">{r.stage_name || "—"}</td>
                  <td className="px-2 py-1 text-slate-600">{r.owner_name || "—"}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.value ? fmt(r.value) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return <p className="text-xs text-slate-400">Tipo desconocido: {widget.type}</p>;
}

// ============================================================
// PANEL EDITOR DIALOG
// ============================================================
function PanelEditorDialog({ panel, pipelines, users = [], onClose, onSave }) {
  const [form, setForm] = useState(panel);
  const [pipelineFieldsCache, setPipelineFieldsCache] = useState({});
  const { getAuthHeaders } = useAuth();

  useEffect(() => { setForm(panel); }, [panel]);

  // Precargar los campos de los pipelines usados para que "Agrupar por" muestre los personalizados
  useEffect(() => {
    const ids = new Set((panel.widgets || []).map((w) => w.pipeline_id).filter(Boolean));
    if (pipelines[0]?.id) ids.add(pipelines[0].id);
    ids.forEach((id) => loadFieldsFor(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFieldsFor = useCallback(async (pipelineId) => {
    if (!pipelineId || pipelineFieldsCache[pipelineId]) return;
    const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}`, getAuthHeaders());
    const d = await r.json();
    setPipelineFieldsCache((c) => ({ ...c, [pipelineId]: d.fields || [] }));
  }, [getAuthHeaders, pipelineFieldsCache]);

  const addWidget = () => {
    const pId = pipelines[0]?.id || "";
    if (pId) loadFieldsFor(pId);
    const id = `w_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setForm((f) => ({
      ...f,
      widgets: [...(f.widgets || []), {
        id, type: "kpi", title: "Nuevo widget", pipeline_id: pId,
        metric: "count", filters: {}, x: 0, y: 0, w: 4, h: 3, color: "#193966",
      }],
    }));
  };

  const updateWidget = (id, patch) => {
    setForm((f) => ({ ...f, widgets: f.widgets.map((w) => w.id === id ? { ...w, ...patch } : w) }));
  };
  const removeWidget = (id) => {
    setForm((f) => ({ ...f, widgets: f.widgets.filter((w) => w.id !== id) }));
  };

  const handleSave = () => {
    if (!form.name?.trim()) { toast.error("El nombre del panel es obligatorio"); return; }
    onSave(form);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="panel-editor-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Editar panel" : "Nuevo panel"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre <span className="text-rose-500">*</span></Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej. Pipeline comercial" data-testid="panel-name" />
          </div>
          <div>
            <Label className="text-xs">Descripción</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Para qué sirve este panel…" />
          </div>
          <div>
            <Label className="text-xs">Pipeline al que pertenece</Label>
            <select
              value={form.pipeline_id || ""}
              onChange={(e) => setForm({ ...form, pipeline_id: e.target.value || null })}
              className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
              data-testid="panel-pipeline"
            >
              <option value="">— Sin pipeline (auto-inferir del primer widget) —</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-0.5">Los paneles se agrupan por este pipeline en la pantalla principal.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_shared} onChange={(e) => setForm({ ...form, is_shared: e.target.checked })} data-testid="panel-shared" />
            Compartir con todos los usuarios
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">¿Quiénes pueden acceder?</Label>
              <select
                value={form.visibility || (form.is_shared ? "shared_view" : "private")}
                onChange={(e) => setForm({ ...form, visibility: e.target.value, is_shared: e.target.value === "shared_view" || e.target.value === "shared_edit" })}
                className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
                data-testid="panel-visibility"
              >
                {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Etiquetas (separadas por coma)</Label>
              <Input
                value={(form.tags || []).join(", ")}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                placeholder="ventas, mensual"
                data-testid="panel-tags"
              />
            </div>
          </div>
          {(form.visibility === "custom") && (
            <div>
              <Label className="text-xs">Usuarios con acceso</Label>
              <div className="border rounded p-2 bg-slate-50 max-h-40 overflow-y-auto grid grid-cols-2 gap-1 mt-1" data-testid="panel-allowed-users">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.allowed_user_ids || []).includes(u.id)}
                      onChange={() => {
                        const cur = form.allowed_user_ids || [];
                        setForm({ ...form, allowed_user_ids: cur.includes(u.id) ? cur.filter((x) => x !== u.id) : [...cur, u.id] });
                      }}
                      className="w-3.5 h-3.5 accent-[#193966]"
                      data-testid={`panel-allowed-${u.id}`}
                    />
                    <span className="truncate">{u.name || u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Widgets ({(form.widgets || []).length})</p>
            <Button size="sm" onClick={addWidget} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="panel-add-widget">
              <Plus className="w-3 h-3 mr-1" /> Agregar widget
            </Button>
          </div>

          {(form.widgets || []).length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-4">Agregá tu primer widget para empezar.</p>
          ) : (
            <div className="space-y-3">
              {form.widgets.map((w, idx) => (
                <Card key={w.id} className="border-slate-200" data-testid={`widget-editor-${idx}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">#{idx + 1}</Badge>
                      <button onClick={() => removeWidget(w.id)} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`widget-remove-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <WidgetEditor
                      widget={{ ...w, _panel_id: form.id }}
                      idx={idx}
                      pipelines={pipelines}
                      pipelineFieldsCache={pipelineFieldsCache}
                      loadFieldsFor={loadFieldsFor}
                      onChange={(patch) => updateWidget(w.id, patch)}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="panel-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// WidgetEditor — wizard 2 tabs (Datos / Presentación) tipo HubSpot Reports
// ============================================================
function WidgetEditor({ widget, idx, pipelines, pipelineFieldsCache, loadFieldsFor, onChange }) {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState("datos");
  const [props, setProps] = useState([]);

  useEffect(() => {
    if (!widget.pipeline_id) return;
    fetch(`${API_URL}/api/pipelines/widget-properties/${widget.pipeline_id}`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setProps(d.items || []))
      .catch(() => setProps([]));
  }, [widget.pipeline_id, getAuthHeaders]);

  const selectedProps = widget.properties_shown || ["title", "stage_name", "owner_name", "value", "expected_close_date"];
  const toggleProp = (key) => {
    const next = selectedProps.includes(key)
      ? selectedProps.filter((k) => k !== key)
      : [...selectedProps, key];
    onChange({ properties_shown: next });
  };

  const propsByCategory = props.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="grid w-full grid-cols-2" data-testid={`widget-tabs-${idx}`}>
        <TabsTrigger value="datos" data-testid={`widget-tab-datos-${idx}`}>
          <Database className="w-3 h-3 mr-1" /> Datos
        </TabsTrigger>
        <TabsTrigger value="presentacion" data-testid={`widget-tab-presentacion-${idx}`}>
          <BarChart3 className="w-3 h-3 mr-1" /> Presentación
        </TabsTrigger>
      </TabsList>

      {/* ============ TAB DATOS ============ */}
      <TabsContent value="datos" className="pt-2">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <Label className="text-[10px]">Pipeline (origen)</Label>
            <select
              value={widget.pipeline_id || ""}
              onChange={(e) => { onChange({ pipeline_id: e.target.value, group_by_field: "" }); loadFieldsFor(e.target.value); }}
              onFocus={() => loadFieldsFor(widget.pipeline_id)}
              className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
              data-testid={`widget-pipeline-${idx}`}
            >
              <option value="">— Seleccionar —</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Título del widget</Label>
            <Input value={widget.title || ""} onChange={(e) => onChange({ title: e.target.value })} data-testid={`widget-title-${idx}`} />
          </div>
        </div>

        <div className="border rounded-md p-2 bg-slate-50">
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
            Propiedades disponibles ({selectedProps.length} seleccionadas)
          </p>
          <p className="text-[10px] text-slate-500 mb-2">
            Marcá las propiedades que querés mostrar en la tabla de datos y en el CSV exportado.
          </p>
          {Object.entries(propsByCategory).map(([cat, items]) => (
            <div key={cat} className="mb-2">
              <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold mb-1">{cat}</p>
              <div className="grid grid-cols-2 gap-1">
                {items.map((p) => (
                  <label key={p.key} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid={`prop-toggle-${p.key}`}>
                    <input
                      type="checkbox"
                      checked={selectedProps.includes(p.key)}
                      onChange={() => toggleProp(p.key)}
                      className="w-3.5 h-3.5 accent-[#193966]"
                    />
                    <span className="truncate">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {widget._panel_id && widget.id && (
          <DataPreviewTable panelId={widget._panel_id} widgetId={widget.id} />
        )}
        {!widget._panel_id && (
          <p className="text-[10px] text-slate-400 italic mt-2">
            Guardá el panel para ver el preview de las filas en tiempo real.
          </p>
        )}
      </TabsContent>

      {/* ============ TAB PRESENTACIÓN ============ */}
      <TabsContent value="presentacion" className="pt-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">Tipo de gráfico</Label>
            <select value={widget.type} onChange={(e) => onChange({ type: e.target.value })} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid={`widget-type-${idx}`}>
              {WIDGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {widget.type !== "tasks_summary" && (
          <div>
            <Label className="text-[10px]">Métrica (medida)</Label>
            <select value={widget.metric || "count"} onChange={(e) => onChange({ metric: e.target.value })} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid={`widget-metric-${idx}`}>
              {METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          )}
          {GROUPABLE_TYPES.includes(widget.type) && (
            <div className="col-span-2">
              <Label className="text-[10px]">Agrupar por</Label>
              <select value={widget.group_by_field || ""} onChange={(e) => onChange({ group_by_field: e.target.value })} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid={`widget-group-by-${idx}`}>
                <option value="">Por defecto (etapa / propietario según tipo)</option>
                <optgroup label="Estándar">
                  {STD_GROUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </optgroup>
                <optgroup label="Campos personalizados">
                  {(pipelineFieldsCache[widget.pipeline_id] || []).map((f) => (
                    <option key={f.id} value={f.name}>{f.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}
          {widget.type === "table" && (
            <div className="col-span-2">
              <Label className="text-[10px]">Columnas de la tabla (con "Agrupar por")</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {TABLE_COLUMNS.map((c) => {
                  const sel = (widget.table_columns && widget.table_columns.length ? widget.table_columns : DEFAULT_TABLE_COLS);
                  const on = sel.includes(c.key);
                  return (
                    <label key={c.key} className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded px-1.5 py-0.5 cursor-pointer bg-white">
                      <input type="checkbox" checked={on} onChange={() => {
                        const next = on ? sel.filter((k) => k !== c.key) : [...sel, c.key];
                        onChange({ table_columns: next.length ? next : ["count"] });
                      }} className="w-3 h-3 accent-[#193966]" data-testid={`widget-tablecol-${c.key}-${idx}`} />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label className="text-[10px]">Ancho (1-12)</Label>
            <Input type="number" min={1} max={12} value={widget.w || 6} onChange={(e) => onChange({ w: Number(e.target.value) || 6 })} />
          </div>
          <div>
            <Label className="text-[10px]">Alto (filas)</Label>
            <Input type="number" min={2} max={10} value={widget.h || 4} onChange={(e) => onChange({ h: Number(e.target.value) || 4 })} />
          </div>
          <div>
            <Label className="text-[10px]">Color principal</Label>
            <Input type="color" value={widget.color || "#193966"} onChange={(e) => onChange({ color: e.target.value })} className="h-9" />
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function DataPreviewTable({ panelId, widgetId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/pipelines/paneles/${panelId}/widgets/${widgetId}/preview?limit=50`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [panelId, widgetId, getAuthHeaders]);

  if (loading) return <p className="text-[10px] text-slate-400 mt-2">Cargando preview…</p>;
  if (!data?.rows?.length) return <p className="text-[10px] text-slate-400 italic mt-2">No hay filas para mostrar con la configuración actual.</p>;

  const props = data.properties || [];
  return (
    <div className="mt-2 border rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 bg-slate-100 border-b">
        <p className="text-[10px] text-slate-600"><b>{data.shown}</b> de <b>{data.total}</b> filas</p>
      </div>
      <div className="overflow-x-auto max-h-[200px]">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 border-b">
            <tr>{props.map((p) => <th key={p} className="text-left px-2 py-1 font-semibold whitespace-nowrap">{p}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 20).map((row) => (
              <tr key={row.id} className="border-b hover:bg-slate-50">
                {props.map((p) => (
                  <td key={p} className="px-2 py-1 whitespace-nowrap max-w-[180px] truncate">
                    {row[p] === null || row[p] === undefined ? "—" : String(row[p])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// PanelsGridLayout — Layout drag-and-drop estilo HubSpot (paneles side-by-side)
// Cada panel es resizable y movible. Persiste posiciones via PATCH /paneles/layout.
// ============================================================
function PanelsGridLayout({ panels, dataByPanel, pipelines, users = [], onRefresh, onWidgetChange, onEdit, onDelete, onLayoutSaved }) {
  const { getAuthHeaders } = useAuth();
  const [editMode, setEditMode] = useState(false);

  // Altura mínima (unidades de grid) para que el contenido del panel se vea completo sin scroll.
  const neededH = useCallback((p) => {
    const ws = p.widgets || [];
    if (!ws.length) return 5;
    if (ws.length === 1) return 8;
    let x = 0, units = 0, rowMax = 0;
    ws.forEach((w) => {
      const span = Math.min(w.w || 6, 12);
      const hh = Math.max(w.h || 4, 2);
      if (x + span > 12) { units += rowMax; x = 0; rowMax = 0; }
      x += span;
      rowMax = Math.max(rowMax, hh);
    });
    units += rowMax;
    const innerPx = units * 80 + Math.max(units - 1, 0) * 12 + 24;
    return Math.ceil((innerPx + 110 + 16) / 66);
  }, []);

  // Build initial layout from each panel's saved layout (fallback: side-by-side flow 2 por fila).
  const buildLayout = useCallback(() => {
    let xCursor = 0;
    let yCursor = 0;
    let rowHeight = 0;
    return panels.map((p) => {
      const saved = p.layout || {};
      const hasSaved = Number.isFinite(saved.w) && Number.isFinite(saved.x) && Number.isFinite(saved.y);
      if (hasSaved) {
        return { i: p.id, x: saved.x, y: saved.y, w: saved.w, h: Math.max(saved.h || 8, neededH(p)), minW: 3, minH: 4 };
      }
      // Auto-flow side-by-side: default w=6, h según contenido (2 paneles por fila en escritorio).
      const w = 6;
      const h = neededH(p);
      if (xCursor + w > 12) {
        // Wrap to next row.
        xCursor = 0;
        yCursor += rowHeight;
        rowHeight = 0;
      }
      const pos = { i: p.id, x: xCursor, y: yCursor, w, h, minW: 3, minH: 4 };
      xCursor += w;
      rowHeight = Math.max(rowHeight, h);
      return pos;
    });
  }, [panels, neededH]);

  const [layout, setLayout] = useState(buildLayout);
  useEffect(() => { setLayout(buildLayout()); }, [buildLayout]);

  const saveLayout = async (newLayout) => {
    try {
      const items = newLayout.map((it) => ({ id: it.i, x: it.x, y: it.y, w: it.w, h: it.h }));
      const r = await fetch(`${API_URL}/api/pipelines/paneles/layout`, {
        method: "PATCH",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) throw new Error();
      toast.success("Posiciones guardadas", { duration: 1200 });
      if (onLayoutSaved) onLayoutSaved();
    } catch { toast.error("Error al guardar layout"); }
  };

  return (
    <>
      <div className="flex items-center justify-end mb-2">
        <Button
          size="sm"
          variant={editMode ? "default" : "outline"}
          onClick={() => setEditMode(!editMode)}
          className={editMode ? "h-8 bg-[#193966] hover:bg-[#152d52] text-white" : "h-8"}
          data-testid="toggle-edit-layout"
        >
          <Move className="w-3.5 h-3.5 mr-1.5" />
          {editMode ? "Salir del modo edición" : "Reordenar paneles"}
        </Button>
      </div>

      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-xs text-amber-800">
          <b>Modo edición:</b> arrastrá los paneles desde su encabezado para moverlos · redimensioná con la esquina inferior derecha · los cambios se guardan automáticamente al soltar.
        </div>
      )}

      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={50}
        isDraggable={editMode}
        isResizable={editMode}
        draggableHandle=".panel-drag-handle"
        margin={[16, 16]}
        containerPadding={[0, 0]}
        onLayoutChange={(l) => setLayout(l)}
        onDragStop={(l) => saveLayout(l)}
        onResizeStop={(l) => saveLayout(l)}
      >
        {panels.map((panel) => (
          <div key={panel.id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
            <PanelCard
              panel={panel}
              widgetsData={dataByPanel[panel.id] || {}}
              pipelines={pipelines}
              users={users}
              onRefresh={(qs) => onRefresh(panel.id, qs)}
              onWidgetChange={() => onWidgetChange(panel.id)}
              onEdit={() => onEdit(panel)}
              onDelete={() => onDelete(panel)}
              dragHandleEnabled={editMode}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </>
  );
}



// ============================================================
// PanelCard — render de un panel con header (Editar/Refrescar/Acciones) + grid de widgets
// ============================================================
function PanelCard({ panel, widgetsData, onRefresh, onEdit, onDelete, pipelines, users = [], onWidgetChange, dragHandleEnabled = false, fullWidth = false }) {
  const { getAuthHeaders } = useAuth();
  const [drilldown, setDrilldown] = useState(null); // { widget, dimensionValue }
  const [customizing, setCustomizing] = useState(null); // widget being customized
  const [showActions, setShowActions] = useState(false);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [widgetActions, setWidgetActions] = useState(null); // widget con menú de acciones abierto
  // Filtros rápidos a nivel de panel (se aplican a TODOS los widgets)
  const [quick, setQuick] = useState({ owner_id: "", created_range: "" });
  // Filtros avanzados por cualquier campo del negocio: {nombreCampo: valor}
  const [adv, setAdv] = useState({});
  const [advField, setAdvField] = useState("");
  const [advValue, setAdvValue] = useState("");
  const [advFields, setAdvFields] = useState([]);
  const panelRef = useRef(null);
  const advPipelineId = (panel.widgets || []).find((w) => w.pipeline_id)?.pipeline_id;

  useEffect(() => {
    if (!advPipelineId) return;
    fetch(`${API_URL}/api/pipelines/${advPipelineId}`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setAdvFields(d.fields || []))
      .catch(() => {});
  }, [advPipelineId, getAuthHeaders]);

  const buildQuickQs = useCallback((q = quick, a = adv) => {
    const p = new URLSearchParams();
    if (q.owner_id) p.set("owner_id", q.owner_id);
    if (q.created_range) p.set("created_range", q.created_range);
    if (Object.keys(a).length) p.set("custom_filters", JSON.stringify(a));
    return p.toString();
  }, [quick, adv]);

  const applyQuick = (patch) => {
    const next = { ...quick, ...patch };
    setQuick(next);
    onRefresh(buildQuickQs(next, adv));
  };

  const applyAdv = (nextAdv) => {
    setAdv(nextAdv);
    onRefresh(buildQuickQs(quick, nextAdv));
  };

  const handleDownloadPanel = async (format) => {
    setShowActions(false);
    if (!panelRef.current) return;
    try {
      toast.info(format === "pdf" ? "Generando PDF del panel…" : "Generando imagen del panel…");
      // Esperar a que el menú de acciones desaparezca del DOM antes de capturar
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 150)));
      const canvas = await html2canvas(panelRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        ignoreElements: (el) => {
          const tid = (el.dataset && el.dataset.testid) || "";
          return tid.startsWith("panel-actions-menu-") || (el.classList && el.classList.contains("fixed") && el.classList.contains("inset-0"));
        },
      });
      const safeName = (panel.name || "panel").replace(/[^\w\sáéíóúñÁÉÍÓÚÑ-]/g, "").replace(/\s+/g, " ").trim() || "panel";
      if (format === "png") {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `${safeName}.png`;
        a.click();
      } else {
        const img = canvas.toDataURL("image/jpeg", 0.85);
        const pdf = new jsPDF({
          orientation: canvas.width >= canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(img, "JPEG", 0, 0, canvas.width, canvas.height, undefined, "FAST");
        pdf.save(`${safeName}.pdf`);
      }
      toast.success("Panel descargado");
    } catch {
      toast.error("Error al generar la descarga del panel");
    }
  };

  const handleExportWidget = async (w) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/widgets/${w.id}/export`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${w.title || "widget"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Widget exportado");
    } catch { toast.error("Error al exportar widget"); }
  };

  const handleExportAll = async () => {
    const widgets = panel.widgets || [];
    if (!widgets.length) { toast.info("Panel sin widgets"); return; }
    for (const w of widgets) await handleExportWidget(w);
    setShowActions(false);
  };

  const handleClone = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/clone`, {
        method: "POST",
        headers: getAuthHeaders().headers,
      });
      if (!r.ok) throw new Error();
      toast.success("Panel clonado");
      setShowActions(false);
      // Forzar reload de la lista de paneles via callback (delegamos al parent via window event)
      window.dispatchEvent(new Event("paneles:reload"));
    } catch { toast.error("Error al clonar"); }
  };

  const handleRename = async () => {
    const newName = window.prompt("Nuevo nombre:", panel.name);
    if (!newName?.trim() || newName === panel.name) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/rename`, {
        method: "PATCH",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!r.ok) throw new Error();
      toast.success("Renombrado");
      setShowActions(false);
      window.dispatchEvent(new Event("paneles:reload"));
    } catch { toast.error("Error al renombrar"); }
  };

  // Tras guardar cambios de un widget, recargar respetando los filtros activos del panel
  const refreshKeepingFilters = () => {
    if (quick.owner_id || quick.created_range || Object.keys(adv).length) {
      setTimeout(() => onRefresh(buildQuickQs()), 450);
    }
  };

  const widgets = panel.widgets || [];

  return (
    <div className="flex flex-col h-full" ref={panelRef} data-testid={`panel-card-${panel.id}`}>
      {/* Header del panel con acciones inline */}
      <div className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2 ${dragHandleEnabled ? "panel-drag-handle cursor-move bg-slate-50" : ""}`}>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {dragHandleEnabled && <Move className="w-4 h-4 text-slate-400 flex-shrink-0" />}
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-800 truncate" data-testid={`panel-name-${panel.id}`}>
              {panel.name}
              <span className="ml-2 text-[10px] uppercase tracking-wider bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5" data-testid={`panel-visibility-badge-${panel.id}`}>
                {visibilityLabel(panel)}
              </span>
              {(panel.tags || []).map((t) => (
                <span key={t} className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5" data-testid={`panel-tag-${panel.id}-${t}`}>
                  <TagIcon className="w-2.5 h-2.5" /> {t}
                </span>
              ))}
            </h2>
            {panel.description && <p className="text-xs text-slate-500 italic mt-0.5 truncate">{panel.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 relative">
          <Button size="sm" variant="outline" onClick={() => onRefresh(buildQuickQs())} className="h-8" title="Refrescar datos" data-testid={`panel-refresh-${panel.id}`}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} className="h-8" title="Editar widgets" data-testid={`panel-edit-${panel.id}`}>
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Editar
          </Button>
          {/* Menú Acciones tipo HubSpot */}
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setShowActions(!showActions)} className="h-8" title="Más acciones" data-testid={`panel-actions-${panel.id}`}>
              <MoreVertical className="w-3.5 h-3.5 mr-1" /> Acciones
            </Button>
            {showActions && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowActions(false)} />
                <div className="absolute right-0 top-9 z-40 bg-white border border-slate-200 rounded-md shadow-lg min-w-[220px] py-1" data-testid={`panel-actions-menu-${panel.id}`}>
                  <button onClick={() => { setShowActions(false); setShowReportBuilder(true); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-report-builder-${panel.id}`}>
                    <Settings className="w-3.5 h-3.5 text-slate-500" /> Personalizar (Report Builder)
                  </button>
                  <div className="border-t my-1" />
                  <button onClick={handleClone} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-clone-${panel.id}`}>
                    <Copy className="w-3.5 h-3.5 text-slate-500" /> Clonar
                  </button>
                  <button onClick={handleRename} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-rename-${panel.id}`}>
                    <TypeIcon className="w-3.5 h-3.5 text-slate-500" /> Cambiar nombre
                  </button>
                  <button onClick={handleExportAll} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-export-all-${panel.id}`}>
                    <Download className="w-3.5 h-3.5 text-slate-500" /> Exportar todos los widgets
                  </button>
                  <button onClick={() => handleDownloadPanel("png")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-download-png-${panel.id}`}>
                    <ImageIcon className="w-3.5 h-3.5 text-slate-500" /> Descargar imagen (PNG)
                  </button>
                  <button onClick={() => handleDownloadPanel("pdf")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`action-download-pdf-${panel.id}`}>
                    <FileText className="w-3.5 h-3.5 text-slate-500" /> Descargar PDF
                  </button>
                  <div className="border-t my-1" />
                  <button onClick={() => { setShowActions(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-rose-50 text-rose-600 flex items-center gap-2" data-testid={`action-delete-${panel.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar el panel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filtros rápidos a nivel panel (afectan todos los widgets) */}
      <div className="px-4 py-1.5 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap" data-testid={`panel-quickfilters-${panel.id}`}>
        <Filter className="w-3 h-3 text-slate-400" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filtros rápidos:</span>
        <select
          value={quick.owner_id}
          onChange={(e) => applyQuick({ owner_id: e.target.value })}
          className="h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white max-w-[160px]"
          data-testid={`panel-quick-owner-${panel.id}`}
        >
          <option value="">Propietario: todos</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <select
          value={quick.created_range}
          onChange={(e) => applyQuick({ created_range: e.target.value })}
          className="h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white"
          data-testid={`panel-quick-range-${panel.id}`}
        >
          {RELATIVE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.value ? `Creados: ${r.label}` : "Creados: todo el tiempo"}</option>)}
        </select>
        {/* Filtro avanzado por cualquier campo del negocio */}
        <select
          value={advField}
          onChange={(e) => { setAdvField(e.target.value); setAdvValue(""); }}
          className="h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white max-w-[180px]"
          data-testid={`panel-adv-field-${panel.id}`}
        >
          <option value="">+ Filtro avanzado…</option>
          {advFields.map((f) => <option key={f.name} value={f.name}>{f.label || f.name}</option>)}
        </select>
        {advField && (() => {
          const fdef = advFields.find((f) => f.name === advField);
          const opts = fdef?.options || [];
          const apply = () => {
            const val = advValue.trim();
            if (!val) return;
            const cur = Array.isArray(adv[advField]) ? adv[advField] : (adv[advField] ? [adv[advField]] : []);
            if (!cur.includes(val)) applyAdv({ ...adv, [advField]: [...cur, val] });
            setAdvValue("");
          };
          return (
            <>
              {opts.length > 0 ? (
                <select value={advValue} onChange={(e) => setAdvValue(e.target.value)} className="h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white max-w-[150px]" data-testid={`panel-adv-value-${panel.id}`}>
                  <option value="">Valor…</option>
                  {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  value={advValue}
                  onChange={(e) => setAdvValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
                  placeholder="Valor…"
                  className="h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white w-[130px]"
                  data-testid={`panel-adv-value-${panel.id}`}
                />
              )}
              <button onClick={apply} className="text-[10px] font-semibold text-[#193966] hover:text-[#80BC00] underline" data-testid={`panel-adv-apply-${panel.id}`}>
                Aplicar
              </button>
            </>
          );
        })()}
        {Object.entries(adv).flatMap(([k, vals]) => (Array.isArray(vals) ? vals : [vals]).map((v) => (
          <span key={`${k}:${v}`} className="inline-flex items-center gap-1 text-[10px] bg-[#193966] text-white rounded-full px-2 py-0.5" data-testid={`panel-adv-chip-${panel.id}-${k}`}>
            {(advFields.find((f) => f.name === k)?.label) || k}: {v}
            <button
              onClick={() => {
                const cur = (Array.isArray(adv[k]) ? adv[k] : [adv[k]]).filter((x) => x !== v);
                const n = { ...adv };
                if (cur.length) n[k] = cur; else delete n[k];
                applyAdv(n);
              }}
              className="hover:text-rose-300 font-bold"
              data-testid={`panel-adv-remove-${panel.id}-${k}`}
            >×</button>
          </span>
        )))}
        {(quick.owner_id || quick.created_range || Object.keys(adv).length > 0) && (
          <button
            onClick={() => { setAdv({}); setAdvField(""); setAdvValue(""); const next = { owner_id: "", created_range: "" }; setQuick(next); onRefresh(buildQuickQs(next, {})); }}
            className="text-[10px] text-rose-600 hover:underline"
            data-testid={`panel-quick-clear-${panel.id}`}
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Widgets */}
      <div className="p-3 flex-1 overflow-auto flex flex-col min-h-0">
        {widgets.length === 0 ? (
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50">
            <p className="text-xs text-slate-500">Este panel no tiene widgets. Click en "Editar" para agregar gráficos.</p>
          </div>
        ) : widgets.length === 1 ? (
          // Single widget → ocupa todo el marco del panel para evitar espacio en blanco.
          <div
            className="bg-white border border-slate-200 rounded-lg shadow-sm p-3 overflow-hidden relative group flex-1 flex flex-col min-h-[260px]"
            data-testid={`widget-${widgets[0].id}`}
          >
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                onClick={() => setCustomizing(widgets[0])}
                className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                title="Personalizar este widget"
                data-testid={`widget-customize-${widgets[0].id}`}
              >
                <Settings className="w-3 h-3 text-slate-600" />
              </button>
              <button
                onClick={() => handleExportWidget(widgets[0])}
                className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                title="Exportar a CSV"
                data-testid={`widget-export-${widgets[0].id}`}
              >
                <Download className="w-3 h-3 text-slate-600" />
              </button>
              <button
                onClick={() => setWidgetActions(widgets[0])}
                className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                title="Más acciones del widget"
                data-testid={`widget-menu-${widgets[0].id}`}
              >
                <MoreVertical className="w-3 h-3 text-slate-600" />
              </button>
            </div>
            <div className={fullWidth ? "h-[420px]" : "flex-1 flex flex-col min-h-0"}>
            <WidgetRenderer
              widget={widgets[0]}
              result={widgetsData[widgets[0].id]}
              onSegmentClick={(dimensionValue) => setDrilldown({ widget: widgets[0], dimensionValue })}
            />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-3 auto-rows-[80px]">
            {widgets.map((w) => {
              const spanW = fullWidth ? ((w.w || 6) > 4 ? 12 : 6) : Math.min(w.w || 6, 12);
              const spanH = fullWidth ? Math.max(w.h || 4, 5) : Math.max(w.h || 4, 2);
              return (
              <div
                key={w.id}
                className="bg-white border border-slate-200 rounded-lg shadow-sm p-3 overflow-hidden relative group"
                style={{ gridColumn: `span ${spanW} / span ${spanW}`, gridRow: `span ${spanH} / span ${spanH}` }}
                data-testid={`widget-${w.id}`}
              >
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={() => setCustomizing(w)}
                    className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                    title="Personalizar este widget"
                    data-testid={`widget-customize-${w.id}`}
                  >
                    <Settings className="w-3 h-3 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleExportWidget(w)}
                    className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                    title="Exportar a CSV"
                    data-testid={`widget-export-${w.id}`}
                  >
                    <Download className="w-3 h-3 text-slate-600" />
                  </button>
                  <button
                    onClick={() => setWidgetActions(w)}
                    className="p-1 bg-white border border-slate-200 rounded shadow-sm hover:bg-slate-50"
                    title="Más acciones del widget"
                    data-testid={`widget-menu-${w.id}`}
                  >
                    <MoreVertical className="w-3 h-3 text-slate-600" />
                  </button>
                </div>
                <WidgetRenderer
                  widget={w}
                  result={widgetsData[w.id]}
                  onSegmentClick={(dimensionValue) => setDrilldown({ widget: w, dimensionValue })}
                />
              </div>
            );})}
          </div>
        )}
      </div>

      {drilldown && (
        <DrillDownDialog
          panelId={panel.id}
          widget={drilldown.widget}
          dimensionValue={drilldown.dimensionValue}
          extraQs={buildQuickQs()}
          onClose={() => setDrilldown(null)}
        />
      )}

      {customizing && (
        <WidgetCustomizeDialog
          panelId={panel.id}
          widget={customizing}
          pipelines={pipelines || []}
          onClose={() => setCustomizing(null)}
          onSaved={() => { setCustomizing(null); if (onWidgetChange) onWidgetChange(); refreshKeepingFilters(); }}
        />
      )}

      {widgetActions && (
        <WidgetActionsDialog
          panel={panel}
          widget={widgetActions}
          onClose={() => setWidgetActions(null)}
          onChanged={() => { setWidgetActions(null); if (onWidgetChange) onWidgetChange(); refreshKeepingFilters(); }}
        />
      )}

      {showReportBuilder && (
        <PanelReportBuilderDialog
          panel={panel}
          onClose={() => setShowReportBuilder(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// WidgetActionsDialog — acciones por widget: clonar, trasladar/copiar a otro panel, eliminar
// ============================================================
function WidgetActionsDialog({ panel, widget, onClose, onChanged }) {
  const { getAuthHeaders } = useAuth();
  const [mode, setMode] = useState(null); // null | "move" | "copy"
  const [targetId, setTargetId] = useState("");
  const [otherPanels, setOtherPanels] = useState([]);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/pipelines/paneles`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setOtherPanels((d.items || []).filter((p) => p.id !== panel.id)))
      .catch(() => {});
  }, [getAuthHeaders, panel.id]);

  const handleClone = async () => {
    setWorking(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/widgets/${widget.id}/clone`, { method: "POST", headers: getAuthHeaders().headers });
      if (!r.ok) { const e = await r.json(); throw new Error(typeof e.detail === "string" ? e.detail : "Error"); }
      toast.success("Widget clonado en este panel");
      window.dispatchEvent(new Event("paneles:reload"));
      onChanged();
    } catch (err) { toast.error(err.message || "Error al clonar el widget"); }
    setWorking(false);
  };

  const handleMoveCopy = async () => {
    if (!targetId) { toast.error("Seleccioná el panel destino"); return; }
    setWorking(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/widgets/${widget.id}/move`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ target_panel_id: targetId, copy: mode === "copy" }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(typeof e.detail === "string" ? e.detail : "Error"); }
      toast.success(mode === "copy" ? "Widget copiado al panel destino" : "Widget trasladado al panel destino");
      window.dispatchEvent(new Event("paneles:reload"));
      onChanged();
    } catch (err) { toast.error(err.message || "Error"); }
    setWorking(false);
  };

  const handleDeleteWidget = async () => {
    if (!window.confirm(`¿Eliminar el widget "${widget.title}" de este panel?`)) return;
    setWorking(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panel.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: panel.name,
          description: panel.description || "",
          pipeline_id: panel.pipeline_id || null,
          widgets: (panel.widgets || []).filter((w) => w.id !== widget.id),
          is_shared: !!panel.is_shared,
          visibility: panel.visibility || (panel.is_shared ? "shared_view" : "private"),
          allowed_user_ids: panel.allowed_user_ids || [],
          tags: panel.tags || [],
        }),
      });
      if (!r.ok) throw new Error();
      toast.success("Widget eliminado");
      window.dispatchEvent(new Event("paneles:reload"));
      onChanged();
    } catch { toast.error("Error al eliminar el widget"); }
    setWorking(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="widget-actions-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Acciones: {widget.title}</DialogTitle>
        </DialogHeader>
        {!mode ? (
          <div className="space-y-1">
            <button onClick={handleClone} disabled={working} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded flex items-center gap-2" data-testid="widget-action-clone">
              <Copy className="w-4 h-4 text-slate-500" /> Clonar en este panel
            </button>
            <button onClick={() => setMode("move")} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded flex items-center gap-2" data-testid="widget-action-move">
              <ArrowRightLeft className="w-4 h-4 text-slate-500" /> Trasladar a otro panel
            </button>
            <button onClick={() => setMode("copy")} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded flex items-center gap-2" data-testid="widget-action-copy">
              <Plus className="w-4 h-4 text-slate-500" /> Agregar (copiar) a otro panel
            </button>
            <div className="border-t my-1" />
            <button onClick={handleDeleteWidget} disabled={working} className="w-full text-left px-3 py-2 text-sm hover:bg-rose-50 text-rose-600 rounded flex items-center gap-2" data-testid="widget-action-delete">
              <Trash2 className="w-4 h-4" /> Eliminar widget
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">{mode === "copy" ? "El widget quedará en ambos paneles." : "El widget se quitará de este panel y pasará al destino."}</p>
            <div>
              <Label className="text-xs">Panel destino</Label>
              <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="widget-move-target">
                <option value="">— Seleccioná un panel —</option>
                {otherPanels.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode(null)}>Anterior</Button>
              <Button onClick={handleMoveCopy} disabled={working} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="widget-move-confirm">
                {working && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {mode === "copy" ? "Copiar" : "Trasladar"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// WidgetCustomizeDialog — Editor focalizado de UN widget (estilo HubSpot "Personalizar")
// Layout 2 columnas: izquierda configuración + propiedades, derecha preview en vivo
// ============================================================
function WidgetCustomizeDialog({ panelId, widget: initial, pipelines, onClose, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [widget, setWidget] = useState(initial);
  const [props, setProps] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);

  // Cargar propiedades disponibles del pipeline
  useEffect(() => {
    if (!widget.pipeline_id) return;
    fetch(`${API_URL}/api/pipelines/widget-properties/${widget.pipeline_id}`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setProps(d.items || [])).catch(() => {});
    // Cargar fields para pie_by_field
    fetch(`${API_URL}/api/pipelines/${widget.pipeline_id}`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setFields(d.fields || [])).catch(() => {});
  }, [widget.pipeline_id, getAuthHeaders]);

  // Cargar preview en vivo cada vez que cambia algo relevante
  useEffect(() => {
    setLoading(true);
    const m = widget.metric || "count";
    fetch(`${API_URL}/api/pipelines/paneles/${panelId}/widgets/${widget.id}/preview?limit=200&metric=${encodeURIComponent(m)}`, getAuthHeaders())
      .then((r) => r.json()).then((d) => { setPreviewData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [panelId, widget.id, widget.properties_shown, widget.metric, getAuthHeaders]);

  // Paginación client-side de la vista previa
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  useEffect(() => { setPage(1); }, [previewData, perPage]);
  const totalRows = previewData?.rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const pagedRows = (previewData?.rows || []).slice((page - 1) * perPage, page * perPage);

  const selectedProps = widget.properties_shown || ["title", "stage_name", "owner_name", "value", "expected_close_date"];
  const toggleProp = (key) => {
    const next = selectedProps.includes(key)
      ? selectedProps.filter((k) => k !== key)
      : [...selectedProps, key];
    setWidget({ ...widget, properties_shown: next });
  };

  const propsByCategory = props.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  const handleSave = async () => {
    // Para guardar un widget individual debemos actualizar el panel completo con
    // el array de widgets reemplazando solo este.
    setSaving(true);
    try {
      // Obtener panel actual
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panelId}`, getAuthHeaders());
      const panel = await r.json();
      const newWidgets = (panel.widgets || []).map((w) => w.id === widget.id ? widget : w);
      const r2 = await fetch(`${API_URL}/api/pipelines/paneles/${panelId}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: panel.name,
          description: panel.description || "",
          pipeline_id: panel.pipeline_id || null,
          widgets: newWidgets,
          is_shared: !!panel.is_shared,
          visibility: panel.visibility || (panel.is_shared ? "shared_view" : "private"),
          allowed_user_ids: panel.allowed_user_ids || [],
          tags: panel.tags || [],
        }),
      });
      if (!r2.ok) throw new Error();
      toast.success("Widget personalizado");
      onSaved();
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="widget-customize-dialog">
        <DialogHeader>
          <DialogTitle>Personalizar: {widget.title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-12 gap-3 flex-1 overflow-hidden">
          {/* IZQUIERDA — Configuración y propiedades */}
          <div className="col-span-5 overflow-y-auto pr-2 space-y-3 border-r border-slate-200">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">Configuración</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <div>
                  <Label className="text-[10px]">Título</Label>
                  <Input value={widget.title || ""} onChange={(e) => setWidget({ ...widget, title: e.target.value })} className="h-8" data-testid="customize-title" />
                </div>
                <div>
                  <Label className="text-[10px]">Pipeline</Label>
                  <select
                    value={widget.pipeline_id || ""}
                    onChange={(e) => setWidget({ ...widget, pipeline_id: e.target.value, group_by_field: "" })}
                    className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white"
                    data-testid="customize-pipeline"
                  >
                    {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                {widget.type !== "tasks_summary" && (
                <div>
                  <Label className="text-[10px]">Métrica</Label>
                  <select value={widget.metric || "count"} onChange={(e) => setWidget({ ...widget, metric: e.target.value })} className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white" data-testid="customize-metric">
                    {METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                )}
                <div className="col-span-2">
                  <Label className="text-[10px]">Tipo de gráfico</Label>
                  <div className="grid grid-cols-5 gap-1.5 mt-1 p-2 bg-slate-50 border border-slate-200 rounded" data-testid="customize-type-grid">
                    {WIDGET_TYPES.map((t) => {
                      const Icon = t.icon;
                      const selected = widget.type === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setWidget({ ...widget, type: t.value })}
                          className={`flex flex-col items-center justify-center gap-1 p-2 rounded border transition-all ${selected ? "border-[#193966] bg-white ring-2 ring-[#193966]/20 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-100"}`}
                          title={t.label}
                          data-testid={`customize-type-${t.value}`}
                        >
                          <Icon className={`w-5 h-5 ${selected ? "text-[#193966]" : "text-slate-500"}`} />
                          <span className={`text-[9px] leading-tight text-center ${selected ? "font-semibold text-[#193966]" : "text-slate-600"}`}>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {GROUPABLE_TYPES.includes(widget.type) && (
                  <div className="col-span-2">
                    <Label className="text-[10px]">Agrupar por</Label>
                    <select value={widget.group_by_field || ""} onChange={(e) => setWidget({ ...widget, group_by_field: e.target.value })} className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white">
                      <option value="">Por defecto (etapa / propietario según tipo)</option>
                      <optgroup label="Estándar">
                        {STD_GROUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </optgroup>
                      <optgroup label="Campos personalizados">
                        {fields.map((f) => <option key={f.id} value={f.name}>{f.label}</option>)}
                      </optgroup>
                    </select>
                  </div>
                )}
                {widget.type === "table" && (
                  <div className="col-span-2">
                    <Label className="text-[10px]">Columnas de la tabla (con "Agrupar por")</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {TABLE_COLUMNS.map((c) => {
                        const sel = (widget.table_columns && widget.table_columns.length ? widget.table_columns : DEFAULT_TABLE_COLS);
                        const on = sel.includes(c.key);
                        return (
                          <label key={c.key} className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded px-1.5 py-0.5 cursor-pointer bg-white">
                            <input type="checkbox" checked={on} onChange={() => {
                              const next = on ? sel.filter((k) => k !== c.key) : [...sel, c.key];
                              setWidget({ ...widget, table_columns: next.length ? next : ["count"] });
                            }} className="w-3 h-3 accent-[#193966]" data-testid={`customize-tablecol-${c.key}`} />
                            {c.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FILTROS DEL INFORME: rango relativo + por cualquier propiedad */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">Filtros del informe</Label>
              <div className="mt-1 space-y-2">
                <div>
                  <Label className="text-[10px]">Creados en (rango relativo)</Label>
                  <select
                    value={(widget.filters || {}).created_range || ""}
                    onChange={(e) => setWidget({ ...widget, filters: { ...(widget.filters || {}), created_range: e.target.value } })}
                    className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white"
                    data-testid="customize-filter-range"
                  >
                    {RELATIVE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px]">Filtrar por propiedad del pipeline</Label>
                  <select
                    value=""
                    onChange={(e) => {
                      const n = e.target.value;
                      if (!n) return;
                      const cur = (widget.filters || {}).custom || {};
                      setWidget({ ...widget, filters: { ...(widget.filters || {}), custom: { ...cur, [n]: "" } } });
                    }}
                    className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white"
                    data-testid="customize-add-propfilter"
                  >
                    <option value="">— Agregar filtro por propiedad —</option>
                    {fields.filter((f) => !Object.prototype.hasOwnProperty.call((widget.filters || {}).custom || {}, f.name)).map((f) => (
                      <option key={f.id} value={f.name}>{f.label}</option>
                    ))}
                  </select>
                  {Object.entries((widget.filters || {}).custom || {}).map(([fname, fval]) => {
                    const fdef = fields.find((x) => x.name === fname);
                    const setVal = (v) => setWidget({ ...widget, filters: { ...(widget.filters || {}), custom: { ...((widget.filters || {}).custom || {}), [fname]: v } } });
                    const removeF = () => {
                      const cur = { ...((widget.filters || {}).custom || {}) };
                      delete cur[fname];
                      setWidget({ ...widget, filters: { ...(widget.filters || {}), custom: cur } });
                    };
                    return (
                      <div key={fname} className="flex items-center gap-1.5 mt-1.5" data-testid={`customize-propfilter-${fname}`}>
                        <span className="text-[11px] text-slate-600 min-w-[90px] truncate">{fdef?.label || fname}:</span>
                        {(fdef?.options || []).length > 0 ? (
                          <select value={fval} onChange={(e) => setVal(e.target.value)} className="flex-1 h-7 border border-slate-300 rounded px-1.5 text-[11px] bg-white" data-testid={`customize-propfilter-value-${fname}`}>
                            <option value="">— Cualquier valor —</option>
                            {fdef.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <Input value={fval} onChange={(e) => setVal(e.target.value)} placeholder="contiene…" className="flex-1 h-7 text-[11px]" data-testid={`customize-propfilter-value-${fname}`} />
                        )}
                        <button onClick={removeF} className="p-0.5 hover:bg-rose-50 rounded text-rose-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-slate-400 mt-1">Los filtros se aplican al guardar el widget.</p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">Propiedades del informe</Label>
              <p className="text-[10px] text-slate-500 mb-1">
                Marcá las columnas que se mostrarán en la tabla y en el CSV.
                Seleccionadas: <b>{selectedProps.length}</b>
              </p>
              <div className="border rounded p-2 bg-slate-50 max-h-[55vh] overflow-y-auto">
                {Object.entries(propsByCategory).map(([cat, items]) => (
                  <div key={cat} className="mb-2">
                    <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold mb-1">{cat}</p>
                    <div className="grid grid-cols-1 gap-0.5">
                      {items.map((p) => (
                        <label key={p.key} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid={`customize-prop-${p.key}`}>
                          <input type="checkbox" checked={selectedProps.includes(p.key)} onChange={() => toggleProp(p.key)} className="w-3.5 h-3.5 accent-[#193966]" />
                          <span className="truncate">{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DERECHA — Preview de la tabla */}
          <div className="col-span-7 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Vista previa de datos {previewData ? <span className="text-slate-400 font-normal">· {previewData.shown} de {previewData.total}</span> : null}
              </Label>
            </div>
            {previewData?.metric && previewData.metric !== "count" && previewData.metric !== "sum_value" && previewData.metric !== "avg_value" && (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                Mostrando solo tarjetas que cuentan para la métrica <b>{METRICS.find((m) => m.value === previewData.metric)?.label || previewData.metric}</b>.
              </p>
            )}
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : !previewData?.rows?.length ? (
              <p className="text-center py-8 text-xs text-slate-400 italic">No hay filas para mostrar con la configuración actual.</p>
            ) : (
              <div className="border rounded overflow-x-auto max-h-[55vh]">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100 border-b sticky top-0">
                    <tr>{selectedProps.map((p) => <th key={p} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{p}</th>)}</tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr key={row.id} className="border-b hover:bg-slate-50">
                        {selectedProps.map((p) => (
                          <td key={p} className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate">
                            {row[p] === null || row[p] === undefined ? "—" : String(row[p])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {totalRows > 0 && (
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2" data-testid="preview-pagination">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
                  <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="h-7 border border-slate-300 rounded px-1 text-[11px] bg-white" data-testid="preview-perpage">
                    {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  filas por página · {(page - 1) * perPage + 1}–{Math.min(page * perPage, totalRows)} de {totalRows}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="preview-prev">Anterior</Button>
                  <span className="text-[11px] text-slate-500 px-1">{page} / {totalPages}</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="preview-next">Siguiente</Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="pt-3 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="customize-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// DrillDownDialog — modal con tabla detalle al click en porción del gráfico
// ============================================================
function DrillDownDialog({ panelId, widget, dimensionValue, onClose, extraQs = "" }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams(extraQs || "");
    if (dimensionValue) qs.set("dimension_value", dimensionValue);
    const url = `${API_URL}/api/pipelines/paneles/${panelId}/widgets/${widget.id}/drilldown${qs.toString() ? `?${qs.toString()}` : ""}`;
    fetch(url, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [panelId, widget.id, dimensionValue, getAuthHeaders]);

  const handleExport = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/paneles/${panelId}/widgets/${widget.id}/export`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${widget.title || "widget"}_drilldown.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportado");
    } catch { toast.error("Error al exportar"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>
              {widget.title}
              {dimensionValue && <span className="ml-2 text-sm font-normal text-slate-500">/ {dimensionValue}</span>}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-600">
            <b>{data?.total || 0}</b> negocios coinciden con esta dimensión
          </p>
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="drilldown-export">
            <Download className="w-3.5 h-3.5 mr-1" /> Exportar
          </Button>
        </div>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : !data?.rows?.length ? (
          <p className="text-center py-8 text-sm text-slate-500">No hay negocios en este segmento.</p>
        ) : (
          <>
          <div className="border rounded overflow-x-auto max-h-[55vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr>{(data.properties || []).map((p) => <th key={p} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{p}</th>)}</tr>
              </thead>
              <tbody>
                {data.rows.slice((page - 1) * perPage, page * perPage).map((row) => (
                  <tr key={row.id} className="border-b hover:bg-slate-50">
                    {(data.properties || []).map((p) => (
                      <td key={p} className="px-2 py-1.5 whitespace-nowrap max-w-[220px] truncate">
                        {row[p] === null || row[p] === undefined ? "—" : String(row[p])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2" data-testid="drilldown-pagination">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }} className="h-7 border border-slate-300 rounded px-1 text-[11px] bg-white">
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              filas por página · {(page - 1) * perPage + 1}–{Math.min(page * perPage, data.rows.length)} de {data.rows.length}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
              <span className="text-[11px] text-slate-500 px-1">{page} / {Math.max(1, Math.ceil(data.rows.length / perPage))}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={page >= Math.ceil(data.rows.length / perPage)} onClick={() => setPage(page + 1)}>Siguiente</Button>
            </div>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// PanelReportBuilderDialog — Report Builder estilo HubSpot a nivel de PANEL.
// Sidebar izquierda con propiedades seleccionadas (drag-reorder + remove) +
// listado a la derecha con catálogo completo y tabla preview con todas las cards
// del pipeline del panel. Botón "Exportar a Excel" descarga .xlsx.
// ============================================================
function PanelReportBuilderDialog({ panel, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(["title", "stage_name", "owner_name", "value", "expected_close_date"]);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [meta, setMeta] = useState({ stages: [], owners: [] });
  const [filters, setFilters] = useState({ stage_id: "", owner_id: "", date_from: "", date_to: "", value_min: "", value_max: "" });

  const pipelineId = panel.pipeline_id || (panel.widgets || [])[0]?.pipeline_id;

  // Cargar catálogo de propiedades disponibles + metadata para filtros
  useEffect(() => {
    if (!pipelineId) return;
    fetch(`${API_URL}/api/pipelines/widget-properties/${pipelineId}`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setCatalog(d.items || [])).catch(() => {});
    fetch(`${API_URL}/api/pipelines/paneles/${panel.id}/report/meta`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setMeta({ stages: d.stages || [], owners: d.owners || [] })).catch(() => {});
  }, [pipelineId, panel.id, getAuthHeaders]);

  // Helper para serializar filtros activos a query params
  const buildFilterQs = useCallback(() => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) qs.append(k, v); });
    return qs.toString();
  }, [filters]);

  // Cargar preview cada vez que cambien las propiedades seleccionadas o los filtros
  useEffect(() => {
    setLoading(true);
    const propsCsv = encodeURIComponent(selected.join(","));
    const filterQs = buildFilterQs();
    const url = `${API_URL}/api/pipelines/paneles/${panel.id}/report?properties=${propsCsv}&limit=100${filterQs ? `&${filterQs}` : ""}`;
    fetch(url, getAuthHeaders())
      .then((r) => r.json()).then((d) => { setPreviewData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [panel.id, selected, buildFilterQs, getAuthHeaders]);

  const activeFiltersCount = Object.values(filters).filter((v) => v !== "" && v !== null && v !== undefined).length;
  const clearFilter = (key) => setFilters((f) => ({ ...f, [key]: "" }));
  const clearAllFilters = () => setFilters({ stage_id: "", owner_id: "", date_from: "", date_to: "", value_min: "", value_max: "" });

  const labelByKey = useMemo(() => {
    const m = {};
    catalog.forEach((p) => { m[p.key] = p.label; });
    return m;
  }, [catalog]);

  const catalogByCategory = useMemo(() => {
    const grouped = {};
    catalog.forEach((p) => {
      if (search && !(p.label.toLowerCase().includes(search.toLowerCase()) || p.key.toLowerCase().includes(search.toLowerCase()))) return;
      (grouped[p.category] = grouped[p.category] || []).push(p);
    });
    return grouped;
  }, [catalog, search]);

  const toggleProp = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const removeProp = (key) => setSelected((prev) => prev.filter((k) => k !== key));

  const moveProp = (from, to) => {
    if (from === to || to < 0 || to >= selected.length) return;
    setSelected((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleExport = async () => {
    if (selected.length === 0) { toast.error("Seleccioná al menos una propiedad"); return; }
    setExporting(true);
    try {
      const propsCsv = encodeURIComponent(selected.join(","));
      const filterQs = buildFilterQs();
      const url = `${API_URL}/api/pipelines/paneles/${panel.id}/report/export?properties=${propsCsv}${filterQs ? `&${filterQs}` : ""}`;
      const r = await fetch(url, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `${(panel.name || "panel").replace(/\s+/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success("Reporte exportado a Excel");
    } catch { toast.error("Error al exportar"); }
    setExporting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden flex flex-col p-0" data-testid="panel-report-builder-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center">
              <Database className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">{panel.name}</h2>
              <p className="text-[10px] text-slate-500">Report Builder · agregá las columnas para tu reporte de Excel</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleExport}
              disabled={exporting || selected.length === 0}
              className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white"
              data-testid="report-export-xlsx-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Exportar a Excel
            </Button>
            <Button size="sm" variant="outline" onClick={onClose} className="h-9">
              <X className="w-4 h-4 mr-1" /> Cerrar
            </Button>
          </div>
        </div>

        {/* Body: 2 columnas */}
        <div className="flex-1 flex overflow-hidden">
          {/* SIDEBAR: Propiedades seleccionadas + catálogo */}
          <aside className="w-[340px] border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Propiedades</h3>
              <p className="text-[11px] text-slate-500">
                Agregá propiedades para generar este informe con los datos que desees. Los datos que selecciones aquí se pueden visualizar a la derecha.
              </p>
            </div>

            {/* Seleccionadas */}
            <div className="p-3 border-b border-slate-200 bg-white">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Propiedades seleccionadas ({selected.length})</p>
              {selected.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">Marcá propiedades del catálogo abajo.</p>
              ) : (
                <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                  {selected.map((key, idx) => (
                    <div
                      key={key}
                      className="flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-1.5 group hover:border-[#193966]"
                      data-testid={`selected-prop-${key}`}
                    >
                      <button
                        onClick={() => moveProp(idx, idx - 1)}
                        disabled={idx === 0}
                        className="text-slate-300 hover:text-slate-600 disabled:opacity-30"
                        title="Subir"
                      >
                        <span className="text-xs leading-none">▲</span>
                      </button>
                      <button
                        onClick={() => moveProp(idx, idx + 1)}
                        disabled={idx === selected.length - 1}
                        className="text-slate-300 hover:text-slate-600 disabled:opacity-30"
                        title="Bajar"
                      >
                        <span className="text-xs leading-none">▼</span>
                      </button>
                      <span className="text-xs text-slate-700 flex-1 truncate">{labelByKey[key] || key}</span>
                      <button
                        onClick={() => removeProp(key)}
                        className="text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Quitar"
                        data-testid={`remove-prop-${key}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Catálogo */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="relative mb-2">
                <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Buscar propiedad…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 text-xs pl-7"
                  data-testid="catalog-search"
                />
              </div>
              {Object.entries(catalogByCategory).map(([cat, items]) => (
                <div key={cat} className="mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold mb-1">{cat}</p>
                  <div className="space-y-0.5">
                    {items.map((p) => (
                      <label key={p.key} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-1.5 py-1" data-testid={`catalog-prop-${p.key}`}>
                        <input
                          type="checkbox"
                          checked={selected.includes(p.key)}
                          onChange={() => toggleProp(p.key)}
                          className="w-3.5 h-3.5 accent-[#193966]"
                        />
                        <span className="truncate text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* CONTENIDO: Tabla preview */}
          <main className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Filtros HubSpot-style */}
            <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap" data-testid="report-filters-bar">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Filtros:</span>
              <select
                value={filters.stage_id}
                onChange={(e) => setFilters((f) => ({ ...f, stage_id: e.target.value }))}
                className="h-7 border border-slate-300 rounded px-2 text-xs bg-white"
                data-testid="filter-stage"
              >
                <option value="">Etapa: todas</option>
                {meta.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select
                value={filters.owner_id}
                onChange={(e) => setFilters((f) => ({ ...f, owner_id: e.target.value }))}
                className="h-7 border border-slate-300 rounded px-2 text-xs bg-white"
                data-testid="filter-owner"
              >
                <option value="">Propietario: todos</option>
                {meta.owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-500">Cierre:</span>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-7 border border-slate-300 rounded px-2 text-xs bg-white"
                  data-testid="filter-date-from"
                />
                <span className="text-[10px] text-slate-400">→</span>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-7 border border-slate-300 rounded px-2 text-xs bg-white"
                  data-testid="filter-date-to"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-500">Valor:</span>
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.value_min}
                  onChange={(e) => setFilters((f) => ({ ...f, value_min: e.target.value }))}
                  className="h-7 w-20 border border-slate-300 rounded px-2 text-xs bg-white"
                  data-testid="filter-value-min"
                />
                <span className="text-[10px] text-slate-400">–</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.value_max}
                  onChange={(e) => setFilters((f) => ({ ...f, value_max: e.target.value }))}
                  className="h-7 w-20 border border-slate-300 rounded px-2 text-xs bg-white"
                  data-testid="filter-value-max"
                />
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-[11px] text-rose-600 hover:text-rose-700 underline"
                  data-testid="filter-clear-all"
                >
                  Borrar filtros ({activeFiltersCount})
                </button>
              )}
            </div>

            {/* Chips de filtros activos */}
            {activeFiltersCount > 0 && (
              <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center gap-1.5 flex-wrap" data-testid="report-active-filters">
                {filters.stage_id && (
                  <span className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full px-2 py-0.5 text-[11px]">
                    Etapa: {meta.stages.find((s) => s.id === filters.stage_id)?.name || filters.stage_id}
                    <button onClick={() => clearFilter("stage_id")} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filters.owner_id && (
                  <span className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full px-2 py-0.5 text-[11px]">
                    Propietario: {meta.owners.find((o) => o.id === filters.owner_id)?.name || filters.owner_id}
                    <button onClick={() => clearFilter("owner_id")} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {(filters.date_from || filters.date_to) && (
                  <span className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full px-2 py-0.5 text-[11px]">
                    Cierre: {filters.date_from || "—"} → {filters.date_to || "—"}
                    <button onClick={() => { clearFilter("date_from"); clearFilter("date_to"); }} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {(filters.value_min || filters.value_max) && (
                  <span className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full px-2 py-0.5 text-[11px]">
                    Valor: {filters.value_min || "0"} – {filters.value_max || "∞"}
                    <button onClick={() => { clearFilter("value_min"); clearFilter("value_max"); }} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}

            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <p className="text-xs text-slate-600">
                {loading ? "Cargando…" : previewData ? (
                  <>Mostrando <b>{previewData.shown}</b> de <b>{previewData.total}</b> tarjetas · {selected.length} columnas</>
                ) : "—"}
              </p>
            </div>
            {loading ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : !previewData?.rows?.length ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-8">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-2">
                  <Database className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-700">No hay tarjetas para mostrar</p>
                <p className="text-[11px] text-slate-500 mt-1">Verificá que el pipeline tenga tarjetas o ajustá las propiedades seleccionadas.</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="w-full text-[12px]" data-testid="report-preview-table">
                  <thead className="bg-slate-100 border-b sticky top-0 z-10">
                    <tr>
                      {selected.map((k) => (
                        <th key={k} className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-600 whitespace-nowrap">
                          {labelByKey[k] || k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row) => (
                      <tr key={row.id} className="border-b hover:bg-slate-50">
                        {selected.map((k) => (
                          <td key={k} className="px-3 py-2 whitespace-nowrap max-w-[260px] truncate text-slate-700">
                            {row[k] === null || row[k] === undefined ? "—" : (Array.isArray(row[k]) ? row[k].join(", ") : String(row[k]))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// METAS DE VENTA — definir metas mensuales por vendedor
// ============================================================
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function MetasDialog({ pipelineId, pipelineName, getAuthHeaders, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [owners, setOwners] = useState([]);
  const [metas, setMetas] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!pipelineId) return;
    setLoading(true);
    try {
      const [ro, rm] = await Promise.all([
        fetch(`${API_URL}/api/pipelines/${pipelineId}/owners`, getAuthHeaders()),
        fetch(`${API_URL}/api/pipelines/${pipelineId}/metas?year=${year}&month=${month}`, getAuthHeaders()),
      ]);
      const dataOwners = await ro.json();
      const dataMetas = await rm.json();
      setOwners(dataOwners.items || []);
      const map = {};
      (dataMetas.items || []).forEach((m) => { map[m.owner_id] = m.meta_value; });
      setMetas(map);
    } catch { toast.error("Error al cargar metas"); }
    finally { setLoading(false); }
  }, [pipelineId, year, month, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveMeta = async (ownerId, value) => {
    setSavingId(ownerId);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/metas`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ owner_id: ownerId, year: Number(year), month: Number(month), meta_value: Number(value) || 0 }),
      });
      if (!r.ok) throw new Error();
      toast.success("Meta guardada");
    } catch { toast.error("Error al guardar la meta"); }
    finally { setSavingId(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="metas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#193966]" /> Metas de venta · {pipelineName || "Pipeline"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1">
            <Label className="text-[10px]">Mes</Label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="metas-month">
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="w-28">
            <Label className="text-[10px]">Año</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9" data-testid="metas-year" />
          </div>
        </div>

        {loading ? (
          <div className="text-center p-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : owners.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No hay vendedores con tarjetas o permisos en este pipeline.</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Vendedor</th>
                  <th className="text-right px-3 py-2 font-semibold w-48">Meta del mes (DOP)</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((o) => (
                  <tr key={o.id} className="border-b hover:bg-slate-50">
                    <td className="px-3 py-2">{o.name}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          min={0}
                          defaultValue={metas[o.id] ?? ""}
                          placeholder="0"
                          onBlur={(e) => saveMeta(o.id, e.target.value)}
                          className="h-8 text-right"
                          data-testid={`meta-input-${o.id}`}
                        />
                        {savingId === o.id && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">
          La meta se guarda automáticamente al salir del campo. El widget <b>Meta vs Logrado</b> compara estas metas con el valor de los negocios ganados en el mes.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="metas-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
